import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import * as i1 from '@angular/material/dialog';

class ScrollableModalCommonService {
    constructor() {
        // The star of this service: the value to adjust for.
        this.amountToAdjustForScrollbarBS$ = new BehaviorSubject(0);
        this.amountToAdjustForScrollbar$ = this.amountToAdjustForScrollbarBS$.asObservable();
        // State
        this.scrollbarRemoved = false;
        this.openedDialogs$ = new BehaviorSubject(new Set());
        // HTML element reference for necessary metrics testing
        this.html = document.documentElement;
    }
    addDialog(dialogRef) {
        this.openedDialogs$.next(this.openedDialogs$.value.add(dialogRef));
    }
    removeDialog(dialogRef) {
        if (this.openedDialogs$.value.delete(dialogRef))
            this.openedDialogs$.next(new Set(this.openedDialogs$.value));
    }
    // Update value
    updateDifference() {
        if (!this.scrollbarRemoved) {
            if (this.openedDialogs$.value.size) {
                const widthWhenItHasAScrollbar = this.html.offsetWidth;
                this.html.classList.add('no-scrollbar');
                const widthWhenItDoesntHaveAScrollbar = this.html.offsetWidth;
                this.amountToAdjustForScrollbarBS$.next(widthWhenItDoesntHaveAScrollbar - widthWhenItHasAScrollbar);
                this.scrollbarRemoved = true;
            }
        }
        else {
            if (!this.openedDialogs$.value.size) {
                this.html.classList.remove('no-scrollbar');
                this.amountToAdjustForScrollbarBS$.next(0);
                this.scrollbarRemoved = false;
            }
        }
    }
}
ScrollableModalCommonService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModalCommonService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ScrollableModalCommonService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModalCommonService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModalCommonService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class ScrollableModal {
    constructor(
    // Dependencies
    dialog, common) {
        this.dialog = dialog;
        this.common = common;
    }
    open(componentOrTemplateRef, config) {
        const dialogRef = this.dialog.open(componentOrTemplateRef, { ...config });
        this.common.addDialog(dialogRef);
        dialogRef.addPanelClass('scrollable-modal');
        let wrapperMouseDownSubscription;
        let wrapperClickSubscription;
        requestAnimationFrame(() => {
            const cdkPane = document.getElementById(dialogRef.id).parentElement;
            const wrapper = cdkPane.parentElement;
            const backdrop = wrapper.previousElementSibling;
            wrapper.classList.add('scrollable-modal-container');
            // Apparently MatDialog scrolls any scrollable containing elements it
            // can find in order to get the dialog as much as possible within the
            // viewport.
            //
            // We don't want that. We want the dialog to always start at the top and
            // with space around it.
            //
            // In order to achieve that, `.scrollable-modal-container
            // .cdk-overlay-pane` elements have `max-height` set to `100%` when created,
            // and the class `actually-scrollable` makes them have `max-height` set to
            // `none` a moment later, once MatDialog has already done its attempt at
            // scrolling scrollable ancestors.
            setTimeout(() => wrapper.classList.add('actually-scrollable'), 600);
            // This stuff down here is commented because it probably doesn't
            // apply any longer.
            //
            // // Safari starts the dialog at the wrong scroll position a lot of times.
            // // The lines right below this comment fix the issue.
            // wrapper.scrollTop = 0;
            // requestAnimationFrame(() => wrapper.scrollTop = 0);
            // setTimeout(() => wrapper.scrollTop = 0);
            // // End of fix.
            this.common.updateDifference();
            let ignoreNextClick = false;
            wrapperMouseDownSubscription = fromEvent(wrapper, 'mousedown')
                .subscribe((e) => ignoreNextClick = e.target !== e.currentTarget);
            wrapperClickSubscription = fromEvent(wrapper, 'click').subscribe((e) => {
                if (!ignoreNextClick && e.target === e.currentTarget) {
                    // Dispatch a click event instead of closing the dialog
                    // in case the close on backdrop click functionality
                    // was disabled for this specific dialog via config.
                    backdrop.dispatchEvent(new Event('click'));
                }
            });
        });
        dialogRef.afterClosed().subscribe(() => {
            if (wrapperMouseDownSubscription)
                wrapperMouseDownSubscription.unsubscribe();
            if (wrapperClickSubscription)
                wrapperClickSubscription.unsubscribe();
            this.common.removeDialog(dialogRef);
            this.common.updateDifference();
        });
        return dialogRef;
    }
}
ScrollableModal.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModal, deps: [{ token: i1.MatDialog }, { token: ScrollableModalCommonService }], target: i0.ɵɵFactoryTarget.Injectable });
ScrollableModal.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModal, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ScrollableModal, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'any' }]
        }], ctorParameters: function () { return [{ type: i1.MatDialog }, { type: ScrollableModalCommonService }]; } });

/*
 * Public API Surface of angular-scrollable-modal
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ScrollableModal, ScrollableModalCommonService };
